import { api, throwApiError } from "../../../api/api";
import type {
  FinAccount,
  FinAccountType,
  FinCashflow,
  FinCategory,
  FinCategoryKind,
  FinCostCenter,
  FinCostCenterGroup,
  FinCounterparty,
  FinDre,
  FinDreBasis,
  FinDashboard,
  FinEntry,
  FinEntryDetail,
  FinEntryList,
  FinEntrySource,
  FinEntryStatus,
  FinEntryType,
  FinFrequency,
  FinPaginated,
  FinPaymentMethod,
  FinRecurrence,
  FinStore,
  FinSummary,
} from "./finTypes";

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

/** Chamada com a mensagem de erro do backend (ou a padrão) em `Error.message`. */
async function call<T>(fn: () => Promise<{ data: T }>, fallback: string): Promise<T> {
  try {
    const { data } = await fn();
    return data;
  } catch (error) {
    throwApiError(error, fallback);
    throw error;
  }
}

// ── Lançamentos ───────────────────────────────────────────────────────────────

export type ListEntriesParams = {
  type?: FinEntryType;
  status?: FinEntryStatus[];
  source?: FinEntrySource[];
  dueFrom?: string;
  dueTo?: string;
  categoryId?: string;
  /** UUID do centro (inclui os rateados nele) ou "none" (sem centro de custo). */
  costCenterId?: string;
  groupId?: string;
  counterpartyId?: string;
  storeId?: string;
  q?: string;
  page?: number;
  limit?: number;
};

export const listEntriesApi = (token: string, params: ListEntriesParams) =>
  call<FinEntryList>(
    () =>
      api.get("/financeiro/entries", {
        ...auth(token),
        params: {
          ...params,
          status: params.status?.length ? params.status.join(",") : undefined,
          source: params.source?.length ? params.source.join(",") : undefined,
        },
      }),
    "Erro ao listar lançamentos."
  );

export const getEntryApi = (token: string, id: string) =>
  call<FinEntryDetail>(() => api.get(`/financeiro/entries/${id}`, auth(token)), "Erro ao carregar o lançamento.");

export type EntryPayload = {
  type: FinEntryType;
  description: string;
  categoryId: string;
  costCenterId?: string | null;
  /** Rateio (2+ centros, soma = valor). Exclusivo com costCenterId; null limpa. */
  allocations?: AllocationPayload[] | null;
  storeId?: string | null;
  counterpartyId?: string | null;
  suggestedAccountId?: string | null;
  competenceDate: string;
  dueDate: string;
  amount: number;
  notes?: string | null;
  installments?: { count: number; interval: FinFrequency };
};

export type AllocationPayload = { costCenterId: string; amount: number };

export const createEntryApi = (token: string, payload: EntryPayload) =>
  call<FinEntry[]>(() => api.post("/financeiro/entries", payload, auth(token)), "Erro ao salvar o lançamento.");

export type UpdateEntryPayload = Partial<Omit<EntryPayload, "type" | "installments">>;

export const updateEntryApi = (token: string, id: string, payload: UpdateEntryPayload) =>
  call<FinEntry>(() => api.patch(`/financeiro/entries/${id}`, payload, auth(token)), "Erro ao salvar o lançamento.");

export const deleteEntryApi = (token: string, id: string) =>
  call<void>(() => api.delete(`/financeiro/entries/${id}`, auth(token)), "Erro ao excluir o lançamento.");

export const cancelEntryApi = (token: string, id: string, reason: string) =>
  call<FinEntry>(
    () => api.post(`/financeiro/entries/${id}/cancel`, { reason }, auth(token)),
    "Erro ao cancelar o lançamento."
  );

// ── Baixas ────────────────────────────────────────────────────────────────────

export type SettlementPayload = {
  accountId: string;
  paidAt: string;
  amount: number;
  method: FinPaymentMethod;
  notes?: string | null;
  assetId?: string | null;
  fileName?: string | null;
};

export const createSettlementApi = (token: string, entryId: string, payload: SettlementPayload) =>
  call<FinEntry>(
    () => api.post(`/financeiro/entries/${entryId}/settlements`, payload, auth(token)),
    "Erro ao registrar a baixa."
  );

export const deleteSettlementApi = (token: string, settlementId: string) =>
  call<FinEntry>(() => api.delete(`/financeiro/settlements/${settlementId}`, auth(token)), "Erro ao estornar a baixa.");

export const uploadFinFileApi = (token: string, file: File) => {
  const form = new FormData();
  form.append("file", file);
  return call<{ assetId: string; fileName: string | null }>(
    () =>
      api.post("/financeiro/files", form, {
        headers: { ...auth(token).headers, "Content-Type": "multipart/form-data" },
      }),
    "Erro ao enviar o comprovante."
  );
};

export const getSettlementFileBlobApi = (token: string, settlementId: string) =>
  call<Blob>(
    () => api.get(`/financeiro/settlements/${settlementId}/file`, { ...auth(token), responseType: "blob" }),
    "Erro ao baixar o comprovante."
  );

// ── Contas bancárias ──────────────────────────────────────────────────────────

export type AccountPayload = {
  name: string;
  type: FinAccountType;
  bankCode?: string | null;
  bankName?: string | null;
  branch?: string | null;
  accountNumber?: string | null;
  initialBalance?: number;
  initialBalanceAt: string;
  isActive?: boolean;
};

export const listAccountsApi = (token: string, includeInactive = false) =>
  call<FinAccount[]>(
    () => api.get("/financeiro/accounts", { ...auth(token), params: includeInactive ? { includeInactive: true } : {} }),
    "Erro ao listar contas."
  );

export const createAccountApi = (token: string, payload: AccountPayload) =>
  call<FinAccount>(() => api.post("/financeiro/accounts", payload, auth(token)), "Erro ao salvar a conta.");

export const updateAccountApi = (token: string, id: string, payload: Partial<AccountPayload>) =>
  call<FinAccount>(() => api.patch(`/financeiro/accounts/${id}`, payload, auth(token)), "Erro ao salvar a conta.");

export const deleteAccountApi = (token: string, id: string) =>
  call<void>(() => api.delete(`/financeiro/accounts/${id}`, auth(token)), "Erro ao excluir a conta.");

// ── Plano de contas ───────────────────────────────────────────────────────────

export const listCategoriesApi = (token: string, params: { kind?: FinCategoryKind; includeInactive?: boolean } = {}) =>
  call<FinCategory[]>(() => api.get("/financeiro/categories", { ...auth(token), params }), "Erro ao carregar o plano de contas.");

export type CategoryPayload = { name: string; kind?: FinCategoryKind; parentId?: string | null; code?: string | null };

export const createCategoryApi = (token: string, payload: CategoryPayload) =>
  call<FinCategory>(() => api.post("/financeiro/categories", payload, auth(token)), "Erro ao salvar a categoria.");

export const updateCategoryApi = (
  token: string,
  id: string,
  payload: { name?: string; code?: string | null; isActive?: boolean }
) => call<FinCategory>(() => api.patch(`/financeiro/categories/${id}`, payload, auth(token)), "Erro ao salvar a categoria.");

export const deleteCategoryApi = (token: string, id: string) =>
  call<void>(() => api.delete(`/financeiro/categories/${id}`, auth(token)), "Erro ao excluir a categoria.");

// ── Fornecedores e clientes ───────────────────────────────────────────────────

export type CounterpartyPayload = {
  name: string;
  document?: string | null;
  email?: string | null;
  phone?: string | null;
  pixKey?: string | null;
  notes?: string | null;
  isActive?: boolean;
};

export const listCounterpartiesApi = (
  token: string,
  params: { q?: string; page?: number; limit?: number; includeInactive?: boolean }
) =>
  call<FinPaginated<FinCounterparty>>(
    () => api.get("/financeiro/counterparties", { ...auth(token), params }),
    "Erro ao listar fornecedores e clientes."
  );

export const createCounterpartyApi = (token: string, payload: CounterpartyPayload) =>
  call<FinCounterparty>(() => api.post("/financeiro/counterparties", payload, auth(token)), "Erro ao salvar o cadastro.");

export const updateCounterpartyApi = (token: string, id: string, payload: Partial<CounterpartyPayload>) =>
  call<FinCounterparty>(
    () => api.patch(`/financeiro/counterparties/${id}`, payload, auth(token)),
    "Erro ao salvar o cadastro."
  );

export const deleteCounterpartyApi = (token: string, id: string) =>
  call<void>(() => api.delete(`/financeiro/counterparties/${id}`, auth(token)), "Erro ao excluir o cadastro.");

// ── Repetições ────────────────────────────────────────────────────────────────

export type RecurrencePayload = {
  type: FinEntryType;
  frequency: FinFrequency;
  startDate: string;
  endDate?: string | null;
  amount: number;
  description: string;
  categoryId: string;
  costCenterId?: string | null;
  allocations?: AllocationPayload[] | null;
  counterpartyId?: string | null;
  accountId?: string | null;
};

export const listRecurrencesApi = (token: string) =>
  call<FinRecurrence[]>(() => api.get("/financeiro/recurrences", auth(token)), "Erro ao listar repetições.");

export const createRecurrenceApi = (token: string, payload: RecurrencePayload) =>
  call<FinRecurrence & { generated: number }>(
    () => api.post("/financeiro/recurrences", payload, auth(token)),
    "Erro ao salvar a repetição."
  );

export type UpdateRecurrencePayload = Partial<
  Pick<RecurrencePayload, "description" | "amount" | "categoryId" | "costCenterId" | "allocations" | "counterpartyId" | "accountId" | "endDate">
> & { isActive?: boolean };

export const updateRecurrenceApi = (token: string, id: string, payload: UpdateRecurrencePayload) =>
  call<FinRecurrence & { updatedEntries: number; removedEntries: number }>(
    () => api.patch(`/financeiro/recurrences/${id}`, payload, auth(token)),
    "Erro ao salvar a repetição."
  );

export const extendRecurrenceApi = (token: string, id: string) =>
  call<FinRecurrence & { generated: number }>(
    () => api.post(`/financeiro/recurrences/${id}/extend`, {}, auth(token)),
    "Erro ao estender a repetição."
  );

// ── Consultas ─────────────────────────────────────────────────────────────────

/** Recorte de títulos do fluxo e do painel. A loja vale também pelo centro de custo de uma loja só. */
export type FinScopeParams = {
  costCenterId?: string;
  groupId?: string;
  storeId?: string;
  categoryId?: string;
  counterpartyId?: string;
  source?: string;
};

export type CashflowParams = FinScopeParams & {
  from: string;
  to: string;
  granularity: "day" | "month";
  accountId?: string;
};

export const getCashflowApi = (token: string, params: CashflowParams) =>
  call<FinCashflow>(() => api.get("/financeiro/cashflow", { ...auth(token), params }), "Erro ao carregar o fluxo de caixa.");

export const getDashboardApi = (token: string, params: CashflowParams & { basis?: "due" | "competence" }) =>
  call<FinDashboard>(() => api.get("/financeiro/dashboard", { ...auth(token), params }), "Erro ao carregar o painel.");

export const getSummaryApi = (token: string) =>
  call<FinSummary>(() => api.get("/financeiro/summary", auth(token)), "Erro ao carregar o resumo.");

export const listCostCentersApi = (token: string) =>
  call<FinCostCenter[]>(() => api.get("/financeiro/cost-centers", auth(token)), "Erro ao listar centros de custo.");

export const listCostCenterGroupsApi = (token: string) =>
  call<FinCostCenterGroup[]>(
    () => api.get("/financeiro/cost-center-groups", auth(token)),
    "Erro ao listar grupos de centro de custo."
  );

// ── DRE ───────────────────────────────────────────────────────────────────────

export type DreParams = { from: string; to: string; basis: FinDreBasis; groupId?: string; costCenterId?: string };

const dreParams = (p: DreParams) => ({
  from: p.from,
  to: p.to,
  basis: p.basis,
  groupId: p.groupId || undefined,
  costCenterId: p.costCenterId || undefined,
});

export const getDreApi = (token: string, params: DreParams) =>
  call<FinDre>(() => api.get("/financeiro/dre", { ...auth(token), params: dreParams(params) }), "Erro ao carregar a DRE.");

export const getDreCsvBlobApi = (token: string, params: DreParams) =>
  call<Blob>(
    () => api.get("/financeiro/dre/export", { ...auth(token), params: dreParams(params), responseType: "blob" }),
    "Erro ao exportar a DRE."
  );

export const listStoresApi = (token: string) =>
  call<FinStore[]>(() => api.get("/financeiro/stores", auth(token)), "Erro ao listar lojas.");
